import { shortDate } from '../lib/format';
import type { ReaderView } from '../lib/reader-view';

interface ReaderChromeProps {
  scopeName: string;
  /**
   * The run that produced this gap, by its own id — `exec-20260805T112411Z-2674`.
   * The date alone does not identify it: a day can carry several runs, and the id
   * is what the run audit is keyed on and what a script or a Slack thread takes.
   */
  executionId: string;
  /** When the draft's execution completed, or undefined if it is unknown. */
  executionCompletedAt: Date | string | undefined;
  /** The draft's 0-based position in the filtered queue, or -1 when filtered out. */
  index: number;
  queueLength: number;
  autoAdvance: boolean;
  /** False until enrichment has finished cleanly and left something populated. */
  isEditable: boolean;
  isEditing: boolean;
  view: ReaderView;
  onJumpToScope: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onToggleAutoAdvance: () => void;
  onToggleEdit: () => void;
  onPreview: () => void;
  onViewChange: (view: ReaderView) => void;
}

/** The two documents, labelled as the reviewer picks between them. */
const VIEW_OPTIONS: { view: ReaderView; label: string; title: string }[] = [
  {
    view: 'product',
    label: 'Product',
    title: 'The gap as a customer would meet it, enrichment and all',
  },
  {
    view: 'report',
    label: 'Run report',
    title: "The run's own gap, exactly as the pipeline wrote it",
  },
];

/**
 * The reader's sticky header: where this draft sits, and how to move.
 *
 * The position crumb can read "not in this filter" — the reviewer keeps a draft
 * while the `todo` filter is active and the row it is on leaves the list under
 * it. Saying so is better than showing a position that no longer exists, and the
 * scope crumb is the way back: it drops the verdict filter.
 *
 * Editing is refused, rather than hidden, until the draft has final fields to
 * offer: the control says why, because a missing button reads as a missing
 * feature.
 * The queue is frozen while the form is open — moving would drop typing that
 * has not been saved — so Prev and Next go with it, and so does the view switch:
 * the form edits the product layer, and swapping the document under it would
 * leave the reviewer editing a page they can no longer see. Preview goes with
 * them for the same reason: it leaves the page, and unsaved edits with it.
 *
 * Preview sits here beside the view switch rather than in the verdict bar
 * because it is a third way to look at the same gap, and the bar below is for
 * the two decisions it exists for.
 */
export function ReaderChrome({
  scopeName,
  executionId,
  executionCompletedAt,
  index,
  queueLength,
  autoAdvance,
  isEditable,
  isEditing,
  view,
  onJumpToScope,
  onPrevious,
  onNext,
  onToggleAutoAdvance,
  onToggleEdit,
  onPreview,
  onViewChange,
}: ReaderChromeProps) {
  return (
    <div className="gr-crumbs">
      <div className="gr-crumb-path">
        <button type="button" onClick={onJumpToScope}>
          {scopeName}
        </button>
        <i>/</i>
        <span className="gr-crumb-run" title={executionId}>
          run <b>{executionId}</b> · {shortDate(executionCompletedAt)}
        </span>
        <i>/</i>
        <b>
          {index === -1
            ? 'not in this filter'
            : `gap ${index + 1} of ${queueLength}`}
        </b>
      </div>

      <div className="gr-crumb-acts">
        <div className="gr-seg" role="group" aria-label="Reader view">
          {VIEW_OPTIONS.map((option) => (
            <button
              key={option.view}
              type="button"
              aria-pressed={view === option.view}
              disabled={isEditing}
              title={option.title}
              onClick={() => onViewChange(option.view)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <span className="gr-kbd" aria-hidden="true">
          R
        </span>
        <i className="gr-row-sep" />
        <button
          type="button"
          disabled={isEditing}
          onClick={onPreview}
          title="Open this gap as a customer would meet it in the app"
        >
          <span className="gr-kbd">P</span> Preview
        </button>
        <i className="gr-row-sep" />
        <button
          type="button"
          onClick={onPrevious}
          disabled={isEditing || index <= 0}
        >
          <span className="gr-kbd">J</span> Prev
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={isEditing || index === -1 || index >= queueLength - 1}
        >
          <span className="gr-kbd">K</span> Next
        </button>
        <i className="gr-row-sep" />
        <button
          type="button"
          aria-pressed={isEditing}
          disabled={!isEditable}
          onClick={onToggleEdit}
          title={
            isEditable
              ? 'Edit the final fields of this draft'
              : 'Nothing final to edit yet — enrichment has to finish cleanly first'
          }
        >
          {isEditing ? 'Stop editing' : 'Edit'}
        </button>
        <i className="gr-row-sep" />
        <button
          className="gr-toggle"
          type="button"
          aria-pressed={autoAdvance}
          disabled={isEditing}
          onClick={onToggleAutoAdvance}
        >
          Auto-advance <i />
        </button>
      </div>
    </div>
  );
}
